import type { ComposerReferenceSnapshot } from './composer-references';

export type ConversationCapability = { engine: 'unified' | 'legacy'; version: 1 };
type ActionContext = {
  forcedTool?: unknown; uiAction?: unknown; sourceSuggestion?: unknown;
  conceptSteering?: unknown; followUpContext?: unknown; targetFormats?: unknown;
  evidenceRecovery?: unknown; silent?: boolean;
};

/** Button payloads retain their existing executor until its adapter has parity. */
export function isStructuredConversationAction(context?: ActionContext): boolean {
  return !!context && (context.forcedTool != null || context.uiAction != null ||
    context.sourceSuggestion != null || context.conceptSteering != null ||
    context.followUpContext != null || context.targetFormats != null ||
    context.evidenceRecovery != null || context.silent === true);
}

export function createConversationCapabilityLoader(
  fetcher: typeof fetch = (input, init) => globalThis.fetch(input, init),
  now: () => number = Date.now,
) {
  const cached = new Map<number, { value: ConversationCapability; until: number }>();
  const pending = new Map<number, Promise<ConversationCapability>>();
  const revisions = new Map<number, number>();
  return async (accountId?: number | null, force = false): Promise<ConversationCapability> => {
    const key = Number.isSafeInteger(accountId) && accountId! > 0 ? accountId! : null;
    if (key != null && !force) {
      const entry = cached.get(key);
      if (entry && entry.until > now()) return entry.value;
      if (pending.has(key)) return pending.get(key)!;
    }
    const revision = key == null ? 0 : (revisions.get(key) ?? 0) + 1;
    if (key != null) { revisions.set(key, revision); if (force) cached.delete(key); }
    const request = (async () => {
      let response: Response | undefined;
      // Retry only this read. A reconnect or a brief deploy outage should not
      // force another click, but we still require a valid engine before POST.
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          response = await fetcher('/api/canvas/conversation-capabilities', {
            credentials: 'include', cache: 'no-store', signal: AbortSignal.timeout(10_000),
          });
        } catch {
          response = undefined;
        }
        if (response && ![502, 503, 504].includes(response.status)) break;
        if (attempt === 0) await new Promise(resolve => setTimeout(resolve, 250));
      }
      if (!response?.ok) throw Error('Could not check chat availability. Your message has not been sent. Please try again.');
      const value = await response.json();
      if (value?.version !== 1 || (value.engine !== 'unified' && value.engine !== 'legacy')) {
        throw Error('Chat availability changed. Your message has not been sent. Refresh and try again.');
      }
      const capability: ConversationCapability = { version: 1, engine: value.engine };
      if (key != null && revisions.get(key) === revision) cached.set(key, { value: capability, until: now() + 5_000 });
      return capability;
    })();
    if (key != null) pending.set(key, request);
    try { return await request; }
    finally { if (key != null && pending.get(key) === request) pending.delete(key); }
  };
}

export const loadConversationCapability = createConversationCapabilityLoader();

/** Attachment metadata stays separate from the user's verbatim instruction. */
export function conversationAttachmentUrls(references?: ComposerReferenceSnapshot): string[] {
  return [...new Set([
    ...(references?.attachedFile ? [references.attachedFile.url] : []),
    ...(references?.chatAttachments ?? []).map(item => item.url),
  ])];
}

export function conversationTurnId(existingId?: string): string {
  return existingId ?? `m-${globalThis.crypto.randomUUID()}`;
}
